import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, message } from 'antd';
import dayjs from 'dayjs';
import { useCanCheck } from '@/ability/helpers';
import {
    getSalaryDistribution,
    getSalaryConfig,
    upsertSalaryConfig,
    updateMonthlySalary,
    updateEmployeeSalary,
    bulkUpdateSalaryStatus,
} from '../services/salary.service';

export const SALARY_QUERY_KEY = ['hr', 'salary', 'distribution'];
export const SALARY_CONFIG_QUERY_KEY = ['hr', 'salary', 'config'];

export const formatCurrency = (val) => {
    const n = parseFloat(val ?? 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
};

export function useSalary() {
    const can = useCanCheck();
    const canView = can('hr.salary.view');
    const canConfigure = can('hr.salary.configure');
    const canUpdate = can('hr.salary.update');

    const queryClient = useQueryClient();
    const [selectedMonth, setSelectedMonth] = useState(() => dayjs().format('YYYY-MM'));
    const [searchText, setSearchText] = useState('');
    const [statusTab, setStatusTab] = useState('all');

    const [configModalOpen, setConfigModalOpen] = useState(false);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [viewingRow, setViewingRow] = useState(null);

    const [configForm] = Form.useForm();
    const [viewForm] = Form.useForm();

    // Fetch salary distribution
    const {
        data: distributionData,
        isLoading,
        isFetching,
        isError,
        refetch,
    } = useQuery({
        queryKey: [...SALARY_QUERY_KEY, selectedMonth],
        queryFn: () => getSalaryDistribution({ month: selectedMonth }),
        enabled: canView,
    });

    // Fetch salary config
    const {
        data: configData,
        isLoading: isConfigLoading,
    } = useQuery({
        queryKey: SALARY_CONFIG_QUERY_KEY,
        queryFn: getSalaryConfig,
        enabled: canView,
    });

    const rawEmployees = useMemo(() => distributionData?.data ?? [], [distributionData]);
    const config = useMemo(() => distributionData?.config ?? configData ?? { absentDeductionRate: '1.00', lateDeductionRate: '0.00' }, [distributionData, configData]);

    // Filter employees by search text and status tab
    const employees = useMemo(() => {
        return rawEmployees.filter((emp) => {
            const matchesSearch = !searchText || (
                (emp.name ?? '').toLowerCase().includes(searchText.toLowerCase()) ||
                (emp.email ?? '').toLowerCase().includes(searchText.toLowerCase())
            );
            const matchesStatus = statusTab === 'all' || (emp.status ?? 'draft') === statusTab;
            return matchesSearch && matchesStatus;
        });
    }, [rawEmployees, searchText, statusTab]);

    // Summary stats
    const summaryStats = useMemo(() => {
        if (!rawEmployees.length) return { totalPayroll: 0, totalDeductions: 0, avgSalary: 0, employeeCount: 0 };
        const totalPayroll = rawEmployees.reduce((sum, e) => sum + parseFloat(e.actualSalary || 0), 0);
        const totalDeductions = rawEmployees.reduce((sum, e) => sum + parseFloat(e.deductions || 0), 0);
        const avgSalary = totalPayroll / rawEmployees.length;
        return {
            totalPayroll,
            totalDeductions,
            avgSalary,
            employeeCount: rawEmployees.length,
        };
    }, [rawEmployees]);

    // Mutation: upsert config
    const configMutation = useMutation({
        mutationFn: upsertSalaryConfig,
        onSuccess: () => {
            message.success('Salary configuration updated');
            queryClient.invalidateQueries({ queryKey: SALARY_CONFIG_QUERY_KEY });
            queryClient.invalidateQueries({ queryKey: SALARY_QUERY_KEY });
            setConfigModalOpen(false);
        },
        onError: () => message.error('Failed to update configuration'),
    });

    // Mutation: update monthly salary
    const updateMonthlyMutation = useMutation({
        mutationFn: ({ userId, month, body }) => updateMonthlySalary(userId, month, body),
        onSuccess: () => {
            message.success('Salary record updated');
            queryClient.invalidateQueries({ queryKey: SALARY_QUERY_KEY });
            setViewModalOpen(false);
            setViewingRow(null);
        },
        onError: () => message.error('Failed to update salary record'),
    });

    // Mutation: bulk status update
    const bulkStatusMutation = useMutation({
        mutationFn: ({ month, status }) => bulkUpdateSalaryStatus({ month, status }),
        onSuccess: (_, variables) => {
            const statusLabel = variables.status === 'pending' ? 'Pending (Waiting for Approval)' : 'Disbursed';
            message.success(`All records for ${variables.month} set to ${statusLabel}`);
            queryClient.invalidateQueries({ queryKey: SALARY_QUERY_KEY });
        },
        onError: () => message.error('Failed to update bulk disbursement status'),
    });

    const handleOpenConfigModal = () => {
        configForm.setFieldsValue({
            absentDeductionRate: parseFloat(config?.absentDeductionRate ?? 1),
            lateDeductionRate: parseFloat(config?.lateDeductionRate ?? 0),
            salaryCycleStartDay: parseInt(config?.salaryCycleStartDay ?? 1, 10),
        });
        setConfigModalOpen(true);
    };

    const handleConfigSubmit = (values) => {
        configMutation.mutate({
            absentDeductionRate: parseFloat(values.absentDeductionRate),
            lateDeductionRate: parseFloat(values.lateDeductionRate),
            salaryCycleStartDay: parseInt(values.salaryCycleStartDay, 10),
        });
    };

    const handleViewRow = (record) => {
        setViewingRow(record);
        const additions = record.additions ?? 0;
        const customDeductions = record.customDeductions ?? 0;
        viewForm.setFieldsValue({
            additions: parseFloat(additions),
            customDeductions: parseFloat(customDeductions),
            actualSalary: parseFloat(record.actualSalary),
            status: record.status ?? 'draft',
            notes: record.notes ?? '',
        });
        setViewModalOpen(true);
    };

    const handleViewSubmit = (values, targetStatus) => {
        const finalStatus = targetStatus || values.status || viewingRow.status || 'draft';
        updateMonthlyMutation.mutate({
            userId: viewingRow.userId,
            month: selectedMonth,
            body: {
                actualSalary: values.actualSalary,
                additions: values.additions,
                customDeductions: values.customDeductions,
                status: finalStatus,
                notes: values.notes,
            },
        });
    };

    const handleMarkStatus = (record, status) => {
        updateMonthlyMutation.mutate({
            userId: record.userId,
            month: selectedMonth,
            body: {
                actualSalary: parseFloat(record.actualSalary),
                status,
                notes: record.notes ?? '',
            },
        });
    };

    const handleBulkDisbursement = (status = 'pending') => {
        bulkStatusMutation.mutate({ month: selectedMonth, status });
    };

    return {
        canView,
        canConfigure,
        canUpdate,
        selectedMonth,
        setSelectedMonth,
        searchText,
        setSearchText,
        statusTab,
        setStatusTab,
        employees,
        rawEmployees,
        config,
        summaryStats,
        isLoading,
        isFetching,
        isError,
        refetch,
        isConfigLoading,
        configModalOpen,
        setConfigModalOpen,
        handleOpenConfigModal,
        handleConfigSubmit,
        configForm,
        configMutation,
        viewingRow,
        viewModalOpen,
        setViewModalOpen,
        handleViewRow,
        handleViewSubmit,
        viewForm,
        updateMonthlyMutation,
        handleMarkStatus,
        handleBulkDisbursement,
        bulkStatusMutation,
        formatCurrency,
    };
}
